import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from "react";
import { getAccessToken, onAccessTokenChange } from '../utils/api';
import { io } from "socket.io-client";
import { useAuth } from "./AuthContext";

const SocketContext = createContext(null);
const SocketInstanceContext = createContext(null); // useSocketInstance

const API_URL = (process.env.REACT_APP_API_URL || "http://localhost:3001/api").replace(/\/api$/, "");

export function SocketProvider({ children }) {
  const { user } = useAuth();
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [socketInstance, setSocketInstance] = useState(null);
  const reconnectAttempt = useRef(0);
  // Live presence map kept in memory: { [userId]: { state, lastActiveAt } }.
  // Updated by `presence:update` broadcasts from the server.
  const [presenceByUserId, setPresenceByUserId] = useState({});

  useEffect(() => {
    const staffRoles = ['admin', 'technician', 'technician_manager', 'sales_manager', 'service_writer', 'sales_support', 'marketing_manager', 'field_representative', 'event_coordinator', 'creative'];
    const token = getAccessToken();

    if (!token || !user || !staffRoles.includes(user.role)) {
      return;
    }

    const socket = io(API_URL, {
      auth: { token },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      reconnectionAttempts: Infinity,
    });

    socket.on("connect", () => {
      setIsConnected(true);
      reconnectAttempt.current = 0;
    });

    socket.on("disconnect", () => {
      setIsConnected(false);
    });

    socket.on("connect_error", (err) => {
      reconnectAttempt.current += 1;
      if (reconnectAttempt.current <= 3) {
        console.warn("[Socket.io] Connection error:", err.message);
      }
    });

    // Receive presence broadcasts from the server (any user in the company)
    socket.on("presence:update", ({ userId, state, lastActiveAt }) => {
      if (!userId) return;
      setPresenceByUserId((prev) => ({
        ...prev,
        [userId]: { state, lastActiveAt },
      }));
    });

    socketRef.current = socket;
    setSocketInstance(socket);

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setSocketInstance(null);
      setIsConnected(false);
    };
    // Key on user.id (not the full user object) so token-refresh-driven
    // /auth/me responses don't churn the socket unnecessarily.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // When the access token rotates (every 3.5h refresh, or after any silent
  // /auth/refresh), update the socket's stored auth payload so the next
  // Socket.io reconnect handshake uses the fresh token. Without this, a
  // reconnect that happens AFTER a refresh (e.g., network blip on hour 4)
  // would attempt to handshake with the now-rotated, possibly-invalidated
  // token and fail authentication — leaving the user silently disconnected.
  //
  // We do NOT forcibly disconnect+reconnect on every rotation. The existing
  // long-lived socket session is already authenticated (server validated JWT
  // at the original handshake; in-band events aren't re-checked). Forcing a
  // reconnect would cause a brief presence flicker every 3.5h for every user.
  useEffect(() => {
    if (!socketInstance) return;
    const updateAuth = (newToken) => {
      if (!socketInstance) return;
      if (newToken) {
        socketInstance.auth = { token: newToken };
      } else {
        // Token cleared (logout from another tab) — close the socket so we
        // don't keep an authenticated session open with no user.
        try { socketInstance.disconnect(); } catch (_) { /* noop */ }
      }
    };
    // Sync once on mount in case the token already rotated before this effect ran.
    updateAuth(getAccessToken());
    return onAccessTokenChange(updateAuth);
  }, [socketInstance]);

  // Heartbeat emit — fires `presence:heartbeat` on REAL user input only,
  // throttled to once per 60 sec. Mirrors AuthContext's activity-listener
  // pattern. Only runs while the socket is connected.
  //
  // We do NOT fire an immediate heartbeat on (re)connect — that would treat
  // socket reconnects (token refresh, network blip, background tab throttling)
  // as user activity and flip an away user back to online silently. The server
  // preserves lastHeartbeatAt across reconnects for the same reason.
  useEffect(() => {
    if (!socketInstance || !isConnected) return;
    let lastSent = 0;
    const THROTTLE_MS = 60 * 1000;
    const handleActivity = () => {
      // Background tabs shouldn't count even if a stray event fires.
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      const now = Date.now();
      if (now - lastSent < THROTTLE_MS) return;
      lastSent = now;
      socketInstance.emit("presence:heartbeat");
    };

    window.addEventListener("mousedown", handleActivity);
    window.addEventListener("keydown", handleActivity);
    window.addEventListener("scroll", handleActivity, true);
    return () => {
      window.removeEventListener("mousedown", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("scroll", handleActivity, true);
    };
  }, [socketInstance, isConnected]);

  const subscribe = useCallback((callRecordId) => {
    if (socketRef.current) {
      socketRef.current.emit("call:subscribe", { callRecordId });
    }
  }, []);

  const unsubscribe = useCallback((callRecordId) => {
    if (socketRef.current) {
      socketRef.current.emit("call:unsubscribe", { callRecordId });
    }
  }, []);

  // Allow consumers (e.g. usePresence hook) to seed initial state from a REST fetch.
  const seedPresence = useCallback((map) => {
    if (!map || typeof map !== "object") return;
    setPresenceByUserId((prev) => ({ ...prev, ...map }));
  }, []);

  // 60s tick so consumers re-render and refresh relative-time strings
  // ("Away — last active 5 minutes ago", "Offline — last seen 2 hours ago").
  // We bump the map reference; data is unchanged so dedup still works elsewhere.
  useEffect(() => {
    const t = setInterval(() => {
      setPresenceByUserId((prev) => ({ ...prev }));
    }, 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const value = useMemo(() => ({
    socket: socketInstance,
    isConnected,
    subscribe,
    unsubscribe,
    presenceByUserId,
    seedPresence,
  }), [socketInstance, isConnected, subscribe, unsubscribe, presenceByUserId, seedPresence]);

  return (
    <SocketContext.Provider value={value}>
      <SocketInstanceContext.Provider value={socketInstance}>
        {children}
      </SocketInstanceContext.Provider>
    </SocketContext.Provider>
  );
}

export function useSocket() {
  return useContext(SocketContext);
}

// Just the socket, for a consumer that only listens (an open board): it
// re-renders when the connection is replaced — not on the presence updates
// and the minute tick that change useSocket()'s value.
export function useSocketInstance() {
  return useContext(SocketInstanceContext);
}
